import {cancelCircle, checkCircle, warningCircle} from '@sail/icons/react/Icon';
import {Action, Icon, media, view} from '@sail/ui';
import {useCallback} from 'react';

type Props = {
  onClose?: () => void;
  status: 'valid' | 'invalid';
  label: string;
};

/**
 * Largely inspired by the @sails/ui/Chip component, with custom icons
 * for the ConnectedAccountChooser.
 */
export const StatusChip = ({onClose, status, label}: Props) => {
  const renderIcon = useCallback(() => {
    if (status === 'valid') {
      return onClose ? (
        <Action data-testid={`close-icon-${label}`} onPress={onClose}>
          <Icon icon={cancelCircle} size="xsmall" />
        </Action>
      ) : (
        <Icon icon={checkCircle} size="xsmall" />
      );
    } else {
      return <Icon icon={warningCircle} size="xsmall" />;
    }
  }, [label, onClose, status]);

  return (
    <view.span
      css={{
        font: 'label.small',
        borderStyle: 'solid',
        borderRadius: 'rounded',
        borderWidth: 'small',
        borderColor: 'border',
        display: 'inline',
        fontWeight: 'semibold',
        stack: 'x',
        alignX: 'center',
        alignY: 'center',
        gap: 'space.75',
        paddingX: 'space.75',
        paddingY: '3px',
        paddingRight: 'small',
        textColor: 'action.primary',
        fill: 'subdued',
        cursor: 'pointer',
        transition: 'all 0.18s',
        minWidth: 'space.0',

        ':hover': {
          backgroundColor: 'offset',
        },

        [media.query('(any-pointer: coarse)')]: {
          font: 'label.medium',
          paddingX: 'space.100',
          paddingRight: 'space.150',
        },
      }}
    >
      <view.span
        css={{
          stack: 'x',
          gap: 'space.75',
          alignX: 'center',
          alignY: 'center',
          minWidth: 'space.0',
        }}
      >
        {renderIcon()}
        <view.span
          css={{
            textColor:
              status === 'valid' ? 'action.primary' : 'action.destructive',
          }}
        >
          {label}
        </view.span>
      </view.span>
    </view.span>
  );
};
